import { useState, useEffect } from 'react';
import { adminService } from '../../services/supabase';
import AdminRestaurantManagement from './AdminRestaurantManagement';
import AdminReports from './AdminReports';

// ── Theme ──────────────────────────────────────────────────────
const T = {
  primary:      '#FF4444',
  primaryDark:  '#e03333',
  primaryLight: '#fff5f5',
  gradient:     'linear-gradient(135deg, #FF4444, #FF6B00)',
  bg:           '#f5f7fa',
  surface:      '#ffffff',
  border:       '#e5e7eb',
  text:         '#1f2937',
  muted:        '#6b7280',
  green:        '#16a34a',
  greenBg:      '#f0fdf4',
  greenBorder:  '#bbf7d0',
  red:          '#dc2626',
  redBg:        '#fef2f2',
  redBorder:    '#fecaca',
  orange:       '#ea580c',
  orangeBg:     '#fff7ed',
};

const AdminDashboard = ({ admin, onLogout }) => {
  const [activeView, setActiveView]                     = useState('restaurants'); // 'restaurants' | 'reports'
  const [selectedRestaurant, setSelectedRestaurant]     = useState(null);
  const [restaurants, setRestaurants]                   = useState([]);
  const [loading, setLoading]                           = useState(true);
  const [showAddRestaurant, setShowAddRestaurant]       = useState(false);
  const [stats, setStats]                               = useState({ total: 0, active: 0, paused: 0 });
  const [posTogglingId, setPosTogglingId]               = useState(null);
  const [toast, setToast]                               = useState(null);
  const [toggleStatusConfirm, setToggleStatusConfirm]   = useState(null);
  const [toggleStatusReason, setToggleStatusReason]     = useState('');
  const [toggleStatusLoading, setToggleStatusLoading]   = useState(false);
  const [resetPasswordRestaurant, setResetPasswordRestaurant] = useState(null);
  const [resetPasswordValue, setResetPasswordValue]     = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordError, setResetPasswordError]     = useState('');
  const [restaurantForm, setRestaurantForm] = useState({
    name: '', address: '', ownerName: '', ownerEmail: '', ownerPhone: '', ownerPassword: '', currency: '₹', timezone: 'Asia/Kolkata'
  });
  const [formErrors, setFormErrors]   = useState({});
  const [submitting, setSubmitting]   = useState(false);

  useEffect(() => {
    document.title = 'Admin — Menuron';
    loadRestaurants();
  }, []);

  const loadRestaurants = async () => {
    try {
      const { data, error } = await adminService.getAllRestaurants();
      if (error) throw error;
      setRestaurants(data || []);
      const total  = data?.length || 0;
      const active = data?.filter(r => r.is_active).length || 0;
      setStats({ total, active, paused: total - active });
    } catch (e) {
      console.error('Error loading restaurants:', e);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const applyPosToggle = async (restaurantId, enabled) => {
    setPosTogglingId(restaurantId);
    const { error } = await adminService.updatePosEnabled(restaurantId, enabled);
    setPosTogglingId(null);
    if (error) {
      showToast('Failed to update Menuron Lite: ' + (error.message || ''), 'error');
    } else {
      setRestaurants(prev => prev.map(r => r.id === restaurantId ? { ...r, menuron_lite: enabled } : r));
      showToast(enabled ? 'Menuron Lite enabled.' : 'Menuron Lite disabled.', 'success');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordValue || resetPasswordValue.length < 6) {
      setResetPasswordError('Password must be at least 6 characters'); return;
    }
    if (resetPasswordValue !== resetPasswordConfirm) {
      setResetPasswordError('Passwords do not match'); return;
    }
    setResetPasswordLoading(true);
    setResetPasswordError('');
    const { error } = await adminService.resetOwnerPassword(resetPasswordRestaurant.id, resetPasswordValue);
    setResetPasswordLoading(false);
    if (error) { setResetPasswordError(error.message || 'Failed to reset password'); return; }
    setResetPasswordRestaurant(null);
    setResetPasswordValue('');
    setResetPasswordConfirm('');
    showToast(`Password reset for ${resetPasswordRestaurant.name}`, 'success');
  };

  const handleManagePosToggled = (restaurantId, newEnabled) => {
    setRestaurants(prev => prev.map(r => r.id === restaurantId ? { ...r, menuron_lite: newEnabled } : r));
  };

  const applyToggleStatus = async () => {
    const { restaurant, newStatus } = toggleStatusConfirm;
    setToggleStatusLoading(true);
    const { error } = await adminService.toggleRestaurantStatus(restaurant.id, newStatus, toggleStatusReason);
    setToggleStatusLoading(false);
    if (error) {
      showToast('Failed to update status: ' + (error.message || ''), 'error');
    } else {
      setRestaurants(prev => prev.map(r => r.id === restaurant.id ? { ...r, is_active: newStatus } : r));
      setStats(prev => ({
        ...prev,
        active: newStatus ? prev.active + 1 : prev.active - 1,
        paused: newStatus ? prev.paused - 1 : prev.paused + 1,
      }));
      showToast(`"${restaurant.name}" ${newStatus ? 'activated' : 'paused'}.`, 'success');
    }
    setToggleStatusConfirm(null);
    setToggleStatusReason('');
  };

  const validateForm = () => {
    const e = {};
    if (!restaurantForm.name.trim())        e.name          = 'Required';
    if (!restaurantForm.address.trim())     e.address       = 'Required';
    if (!restaurantForm.ownerName.trim())   e.ownerName     = 'Required';
    if (!restaurantForm.ownerEmail.trim())  e.ownerEmail    = 'Required';
    if (!restaurantForm.ownerPhone.trim())  e.ownerPhone    = 'Required';
    if (!restaurantForm.ownerPassword.trim()) e.ownerPassword = 'Required';
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAddRestaurant = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const { data, error } = await adminService.createRestaurant(
        { name: restaurantForm.name, address: restaurantForm.address, default_currency: restaurantForm.currency, timezone: restaurantForm.timezone },
        { fullName: restaurantForm.ownerName, email: restaurantForm.ownerEmail, phone: restaurantForm.ownerPhone, password: restaurantForm.ownerPassword }
      );
      if (error) { setFormErrors({ general: error.message }); return; }
      setRestaurantForm({ name: '', address: '', ownerName: '', ownerEmail: '', ownerPhone: '', ownerPassword: '', currency: '₹', timezone: 'Asia/Kolkata' });
      setFormErrors({});
      setShowAddRestaurant(false);
      loadRestaurants();
      showToast(`"${data.restaurant.name}" created successfully!`, 'success');
    } catch {
      setFormErrors({ general: 'Failed to create restaurant. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (selectedRestaurant) {
    return (
      <AdminRestaurantManagement
        restaurant={selectedRestaurant}
        onBack={() => setSelectedRestaurant(null)}
        onPosToggled={handleManagePosToggled}
      />
    );
  }

  if (loading) return <FullPageLoader />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} />}

      {/* Top Header */}
      <header style={{
        background: T.surface, borderBottom: `1px solid ${T.border}`,
        padding: '0 32px', height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src="https://i.ibb.co/hF3Q8YrX/menuron-high-resolution-logo.png"
            alt="Menuron"
            style={{ height: '52px', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))' }}
          />
          <div style={{ fontSize: '11px', fontWeight: '600', color: T.muted, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Admin Portal</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: T.primaryLight, borderRadius: '8px', padding: '6px 12px',
          }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%',
              background: T.gradient, color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', fontWeight: '800',
            }}>
              {admin.full_name?.[0]?.toUpperCase() || 'A'}
            </div>
            <span style={{ fontSize: '13px', fontWeight: '600', color: T.primary }}>{admin.full_name}</span>
          </div>
          {activeView === 'restaurants' && (
          <button
            onClick={() => setShowAddRestaurant(true)}
            style={{
              background: T.gradient, color: 'white', border: 'none',
              padding: '9px 18px', borderRadius: '8px',
              fontSize: '13px', fontWeight: '700', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(255,68,68,0.30)',
            }}
          >+ Add Restaurant</button>
          )}
          <button
            onClick={onLogout}
            style={{
              background: 'transparent', color: T.muted,
              border: `1.5px solid ${T.border}`,
              padding: '9px 16px', borderRadius: '8px',
              fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            }}
          >Logout</button>
        </div>
      </header>

      {/* View switcher */}
      <div style={{ maxWidth: '1300px', margin: '0 auto', padding: '20px 24px 0', display: 'flex', gap: '8px' }}>
        {[
          { key: 'restaurants', label: 'Restaurants' },
          { key: 'reports', label: 'Reports' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveView(key)}
            style={{
              background: activeView === key ? T.primaryLight : 'transparent',
              color: activeView === key ? T.primary : T.muted,
              border: 'none', borderRadius: '8px',
              padding: '8px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
            }}
          >{label}</button>
        ))}
      </div>

      {/* Page body */}
      <div style={{ maxWidth: '1300px', margin: '0 auto', padding: '32px 24px' }}>

        {activeView === 'reports' && <AdminReports />}

        {activeView === 'restaurants' && (
        <>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '32px' }}>
          <StatCard label="Total Restaurants" value={stats.total}  color={T.primary} />
          <StatCard label="Active"             value={stats.active} color={T.green}   />
          <StatCard label="Paused"             value={stats.paused} color={T.orange}  />
        </div>

        {/* Restaurant list */}
        <div style={{
          background: T.surface, borderRadius: '16px',
          border: `1px solid ${T.border}`,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}>
          {/* Panel header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '20px 24px', borderBottom: `1px solid ${T.border}`,
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: T.text }}>All Restaurants</h2>
              <p style={{ margin: '2px 0 0', fontSize: '13px', color: T.muted }}>{restaurants.length} total</p>
            </div>
          </div>

          {restaurants.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 40px', color: T.muted }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
              <h3 style={{ color: T.text, margin: '0 0 8px', fontSize: '16px' }}>No Restaurants Yet</h3>
              <p style={{ margin: 0, fontSize: '14px' }}>Click "+ Add Restaurant" to get started.</p>
            </div>
          ) : (
            <div>
              {restaurants.map((restaurant, idx) => (
                <RestaurantRow
                  key={restaurant.id}
                  restaurant={restaurant}
                  isLast={idx === restaurants.length - 1}
                  posTogglingId={posTogglingId}
                  onPosToggle={(r, v) => applyPosToggle(r.id, v)}
                  onResetPassword={r => { setResetPasswordRestaurant(r); setResetPasswordValue(''); setResetPasswordConfirm(''); setResetPasswordError(''); }}
                  onManage={r => setSelectedRestaurant(r)}
                  onToggleStatus={(r) => { setToggleStatusConfirm({ restaurant: r, newStatus: !r.is_active }); setToggleStatusReason(''); }}
                />
              ))}
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {/* ── Toggle Status Modal ── */}
      {toggleStatusConfirm && (
        <Overlay>
          <ModalCard>
            <h3 style={ms.modalTitle}>
              {toggleStatusConfirm.newStatus ? 'Activate' : 'Pause'} "{toggleStatusConfirm.restaurant.name}"?
            </h3>
            <p style={ms.modalSubtitle}>
              {toggleStatusConfirm.newStatus
                ? 'This will restore full access for this restaurant.'
                : 'This will block access for this restaurant.'}
            </p>
            <label style={ms.label}>Reason (optional)</label>
            <input
              style={ms.input}
              placeholder="Enter reason…"
              value={toggleStatusReason}
              onChange={e => setToggleStatusReason(e.target.value)}
            />
            <div style={ms.actions}>
              <button style={ms.cancelBtn} onClick={() => setToggleStatusConfirm(null)} disabled={toggleStatusLoading}>Cancel</button>
              <button
                style={{ ...ms.confirmBtn, background: toggleStatusConfirm.newStatus ? T.green : T.orange, opacity: toggleStatusLoading ? 0.6 : 1 }}
                onClick={applyToggleStatus}
                disabled={toggleStatusLoading}
              >
                {toggleStatusLoading ? 'Saving…' : (toggleStatusConfirm.newStatus ? 'Activate' : 'Pause')}
              </button>
            </div>
          </ModalCard>
        </Overlay>
      )}

      {/* ── Reset Password Modal ── */}
      {resetPasswordRestaurant && (
        <Overlay>
          <ModalCard>
            <h3 style={ms.modalTitle}>Reset Owner Password</h3>
            <p style={ms.modalSubtitle}>{resetPasswordRestaurant.name} · {resetPasswordRestaurant.restaurant_owners?.email}</p>
            {resetPasswordError && <div style={ms.errorBox}>{resetPasswordError}</div>}
            <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
              <div>
                <label style={ms.label}>New Password</label>
                <input type="password" style={ms.input} placeholder="Min. 6 characters"
                  value={resetPasswordValue} onChange={e => setResetPasswordValue(e.target.value)} />
              </div>
              <div>
                <label style={ms.label}>Confirm Password</label>
                <input type="password" style={ms.input} placeholder="Re-enter password"
                  value={resetPasswordConfirm} onChange={e => setResetPasswordConfirm(e.target.value)} />
              </div>
            </div>
            <div style={ms.actions}>
              <button style={ms.cancelBtn} onClick={() => setResetPasswordRestaurant(null)} disabled={resetPasswordLoading}>Cancel</button>
              <button
                style={{ ...ms.confirmBtn, background: T.primary, opacity: resetPasswordLoading ? 0.6 : 1 }}
                onClick={handleResetPassword}
                disabled={resetPasswordLoading}
              >
                {resetPasswordLoading ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </ModalCard>
        </Overlay>
      )}

      {/* ── Add Restaurant Modal ── */}
      {showAddRestaurant && (
        <Overlay>
          <ModalCard wide>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: T.text }}>Add New Restaurant</h3>
              <button onClick={() => setShowAddRestaurant(false)} style={{ background: 'none', border: 'none', fontSize: '20px', color: T.muted, cursor: 'pointer' }}>×</button>
            </div>

            {formErrors.general && <div style={ms.errorBox}>{formErrors.general}</div>}

            <SectionLabel>Restaurant Info</SectionLabel>
            <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
              <FormField label="Restaurant Name *" error={formErrors.name}>
                <input style={fieldStyle(formErrors.name)} placeholder="e.g. The Grand Cafe"
                  value={restaurantForm.name} onChange={e => setRestaurantForm({ ...restaurantForm, name: e.target.value })} />
              </FormField>
              <FormField label="Address *" error={formErrors.address}>
                <textarea style={{ ...fieldStyle(formErrors.address), resize: 'vertical' }} rows={2}
                  placeholder="Full address"
                  value={restaurantForm.address} onChange={e => setRestaurantForm({ ...restaurantForm, address: e.target.value })} />
              </FormField>
              <FormField label="Default Currency">
                <select style={fieldStyle(false)} value={restaurantForm.currency}
                  onChange={e => setRestaurantForm({ ...restaurantForm, currency: e.target.value })}>
                  <option value="₹">₹ Indian Rupee</option>
                  <option value="$">$ US Dollar</option>
                  <option value="£">£ British Pound</option>
                  <option value="€">€ Euro</option>
                  <option value="AED">AED UAE Dirham</option>
                  <option value="S$">S$ Singapore Dollar</option>
                  <option value="A$">A$ Australian Dollar</option>
                  <option value="C$">C$ Canadian Dollar</option>
                </select>
              </FormField>
              <FormField label="Timezone">
                <select style={fieldStyle(false)} value={restaurantForm.timezone}
                  onChange={e => setRestaurantForm({ ...restaurantForm, timezone: e.target.value })}>
                  <option value="Asia/Kolkata">Asia/Kolkata (IST +5:30)</option>
                  <option value="Asia/Dubai">Asia/Dubai (GST +4:00)</option>
                  <option value="Asia/Singapore">Asia/Singapore (SGT +8:00)</option>
                  <option value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur (MYT +8:00)</option>
                  <option value="Asia/Bangkok">Asia/Bangkok (ICT +7:00)</option>
                  <option value="Asia/Jakarta">Asia/Jakarta (WIB +7:00)</option>
                  <option value="Asia/Manila">Asia/Manila (PST +8:00)</option>
                  <option value="Asia/Colombo">Asia/Colombo (SLST +5:30)</option>
                  <option value="Asia/Dhaka">Asia/Dhaka (BST +6:00)</option>
                  <option value="Asia/Karachi">Asia/Karachi (PKT +5:00)</option>
                  <option value="Europe/London">Europe/London (GMT/BST)</option>
                  <option value="Europe/Paris">Europe/Paris (CET +1:00)</option>
                  <option value="America/New_York">America/New_York (ET)</option>
                  <option value="America/Chicago">America/Chicago (CT)</option>
                  <option value="America/Denver">America/Denver (MT)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (PT)</option>
                  <option value="Australia/Sydney">Australia/Sydney (AEDT +11:00)</option>
                  <option value="Pacific/Auckland">Pacific/Auckland (NZDT +13:00)</option>
                </select>
              </FormField>
            </div>

            <SectionLabel>Owner Info</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '28px' }}>
              <FormField label="Owner Name *" error={formErrors.ownerName}>
                <input style={fieldStyle(formErrors.ownerName)} placeholder="Full name"
                  value={restaurantForm.ownerName} onChange={e => setRestaurantForm({ ...restaurantForm, ownerName: e.target.value })} />
              </FormField>
              <FormField label="Owner Email *" error={formErrors.ownerEmail}>
                <input type="email" style={fieldStyle(formErrors.ownerEmail)} placeholder="email@example.com"
                  value={restaurantForm.ownerEmail} onChange={e => setRestaurantForm({ ...restaurantForm, ownerEmail: e.target.value })} />
              </FormField>
              <FormField label="Phone *" error={formErrors.ownerPhone}>
                <input type="tel" style={fieldStyle(formErrors.ownerPhone)} placeholder="+91 9999999999"
                  value={restaurantForm.ownerPhone} onChange={e => setRestaurantForm({ ...restaurantForm, ownerPhone: e.target.value })} />
              </FormField>
              <FormField label="Password *" error={formErrors.ownerPassword}>
                <input type="password" style={fieldStyle(formErrors.ownerPassword)} placeholder="Min. 6 characters"
                  value={restaurantForm.ownerPassword} onChange={e => setRestaurantForm({ ...restaurantForm, ownerPassword: e.target.value })} />
              </FormField>
            </div>

            <div style={ms.actions}>
              <button style={ms.cancelBtn} onClick={() => setShowAddRestaurant(false)} disabled={submitting}>Cancel</button>
              <button
                style={{ ...ms.confirmBtn, background: T.gradient, opacity: submitting ? 0.6 : 1 }}
                onClick={handleAddRestaurant}
                disabled={submitting}
              >
                {submitting ? 'Creating…' : 'Create Restaurant'}
              </button>
            </div>
          </ModalCard>
        </Overlay>
      )}
    </div>
  );
};

// ── Restaurant Row ─────────────────────────────────────────────
const RestaurantRow = ({ restaurant, isLast, posTogglingId, onPosToggle, onResetPassword, onManage, onToggleStatus }) => {
  const isActive = restaurant.is_active;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '16px',
      padding: '18px 24px',
      borderBottom: isLast ? 'none' : `1px solid ${T.border}`,
      transition: 'background 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {/* Avatar */}
      <div style={{
        width: '42px', height: '42px', borderRadius: '12px', flexShrink: 0,
        background: isActive ? T.primaryLight : '#f3f4f6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '18px', fontWeight: '800', color: isActive ? T.primary : T.muted,
      }}>
        {restaurant.name?.[0]?.toUpperCase()}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: '700', color: T.text, marginBottom: '2px' }}>{restaurant.name}</div>
        <div style={{ fontSize: '12px', color: T.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {restaurant.restaurant_owners?.full_name} · {restaurant.restaurant_owners?.email}
        </div>
        <div style={{ fontSize: '11px', color: T.muted, marginTop: '1px' }}>
          Since {new Date(restaurant.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </div>
      </div>

      {/* Status badge */}
      <span style={{
        padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
        background: isActive ? T.greenBg : T.redBg,
        color: isActive ? T.green : T.red,
        border: `1px solid ${isActive ? T.greenBorder : T.redBorder}`,
        whiteSpace: 'nowrap',
      }}>
        {isActive ? 'ACTIVE' : 'PAUSED'}
      </span>

      {/* Menuron Lite toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
        <span style={{ fontSize: '11px', fontWeight: '600', color: T.muted }}>Lite</span>
        <button
          onClick={() => onPosToggle(restaurant, !restaurant.menuron_lite)}
          disabled={posTogglingId === restaurant.id}
          aria-label={restaurant.menuron_lite ? 'Disable Menuron Lite' : 'Enable Menuron Lite'}
          style={{
            position: 'relative', display: 'inline-flex', alignItems: 'center',
            width: '40px', height: '22px', borderRadius: '11px', border: 'none',
            background: restaurant.menuron_lite ? T.green : '#d1d5db',
            cursor: posTogglingId === restaurant.id ? 'not-allowed' : 'pointer',
            padding: 0, transition: 'background 0.25s',
            opacity: posTogglingId === restaurant.id ? 0.5 : 1,
          }}
        >
          <span style={{
            position: 'absolute', top: '2px',
            left: restaurant.menuron_lite ? '20px' : '2px',
            width: '18px', height: '18px', borderRadius: '50%',
            background: 'white', transition: 'left 0.25s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </button>
        <span style={{ fontSize: '11px', fontWeight: '700', color: restaurant.menuron_lite ? T.green : T.muted, minWidth: '22px' }}>
          {posTogglingId === restaurant.id ? '…' : restaurant.menuron_lite ? 'ON' : 'OFF'}
        </span>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        <RowBtn label="Reset Password" color="#7c3aed" onClick={() => onResetPassword(restaurant)} />
        <RowBtn label="Manage →" color={T.primary} onClick={() => onManage(restaurant)} />
        <RowBtn
          label={isActive ? 'Pause' : 'Activate'}
          color={isActive ? T.orange : T.green}
          onClick={() => onToggleStatus(restaurant)}
        />
      </div>
    </div>
  );
};

// ── Small components ───────────────────────────────────────────
const StatCard = ({ label, value, color }) => (
  <div style={{
    background: T.surface, borderRadius: '14px', padding: '24px',
    border: `1px solid ${T.border}`,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  }}>
    <div style={{ fontSize: '28px', fontWeight: '800', color, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '13px', color: T.muted, marginTop: '6px', fontWeight: '600' }}>{label}</div>
  </div>
);

const RowBtn = ({ label, color, onClick }) => (
  <button onClick={onClick} style={{
    background: 'transparent', color, border: `1.5px solid ${color}`,
    padding: '6px 12px', borderRadius: '7px', cursor: 'pointer',
    fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap',
    transition: 'all 0.15s',
  }}
    onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = 'white'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = color; }}
  >
    {label}
  </button>
);

const Overlay = ({ children }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, backdropFilter: 'blur(4px)',
  }}>{children}</div>
);

const ModalCard = ({ children, wide }) => (
  <div style={{
    background: T.surface, borderRadius: '20px',
    padding: '32px', width: '90%',
    maxWidth: wide ? '600px' : '460px',
    maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
  }}>{children}</div>
);

const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: '11px', fontWeight: '700', color: T.muted,
    textTransform: 'uppercase', letterSpacing: '0.8px',
    marginBottom: '10px',
  }}>{children}</div>
);

const FormField = ({ label, error, children }) => (
  <div>
    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: T.text }}>{label}</label>
    {children}
    {error && <div style={{ color: T.red, fontSize: '11px', marginTop: '4px' }}>{error}</div>}
  </div>
);

const Toast = ({ message, type }) => (
  <div style={{
    position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
    background: type === 'success' ? T.greenBg : T.redBg,
    border: `1px solid ${type === 'success' ? T.greenBorder : T.redBorder}`,
    color: type === 'success' ? T.green : T.red,
    borderRadius: '10px', padding: '12px 18px',
    fontSize: '13px', fontWeight: '600',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    maxWidth: '380px',
  }}>{message}</div>
);

const FullPageLoader = () => (
  <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '44px', height: '44px', borderRadius: '50%',
        border: `3px solid ${T.primaryLight}`, borderTopColor: T.primary,
        margin: '0 auto 16px', animation: 'spin 0.8s linear infinite',
      }} />
      <div style={{ fontSize: '14px', color: T.muted, fontWeight: '600' }}>Loading…</div>
    </div>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ── Shared styles for modals ───────────────────────────────────
const ms = {
  modalTitle:   { margin: '0 0 6px', fontSize: '17px', fontWeight: '800', color: T.text },
  modalSubtitle:{ margin: '0 0 20px', fontSize: '13px', color: T.muted },
  label:        { display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '700', color: T.text },
  input:        { width: '100%', padding: '10px 12px', border: `1.5px solid ${T.border}`, borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', color: T.text },
  actions:      { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' },
  cancelBtn:    { padding: '10px 20px', border: `1.5px solid ${T.border}`, background: 'transparent', color: T.muted, borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  confirmBtn:   { padding: '10px 20px', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
  errorBox:     { background: T.redBg, border: `1px solid ${T.redBorder}`, color: T.red, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', fontWeight: '600' },
};

const fieldStyle = (hasError) => ({
  width: '100%', padding: '10px 12px',
  border: `1.5px solid ${hasError ? T.red : T.border}`,
  borderRadius: '8px', fontSize: '14px',
  boxSizing: 'border-box', outline: 'none', color: T.text,
  background: T.surface,
});

export default AdminDashboard;
